import type { Appointment, OpeningWindow, TimeBlock, TimeOff } from '../api/client'
import { weekdayIndex } from './dates'

export type DayRow =
  | { kind: 'booking'; start: number; appointment: Appointment }
  | { kind: 'free'; start: number; end: number }
  | { kind: 'break'; start: number; end: number }
  | { kind: 'blocked'; start: number; end: number }
  | { kind: 'timeBlock'; start: number; end: number; block: TimeBlock }

export interface DayPlan {
  closed: boolean
  timeOff: TimeOff | null
  rows: DayRow[]
  count: number
  bookedPence: number
}

// Free gaps shorter than this can't take a booking, so they aren't shown.
// The API draws the same stretches for Today's free count (schedule.FreeStretches).
const minFreeMinutes = 15

// onTheBook is what a day's or week's summary counts: booked and completed
// appointments ("5 appointments · £318"). A no-show or a cancellation is
// still drawn, but neither counts nor earns (as Today's metrics, QA 2026-10-14).
export function onTheBook(a: Pick<Appointment, 'status'>): boolean {
  return a.status === 'booked' || a.status === 'completed'
}

// bookedSummary is the count and money of what is on the book. A visit a
// package session pays for adds £0, as its row shows: that money came in when
// the package was sold.
export function bookedSummary(appointments: Appointment[]): { count: number; bookedPence: number } {
  const counted = appointments.filter(onTheBook)
  return { count: counted.length, bookedPence: counted.reduce((sum, a) => sum + (a.payment_state === 'package' ? 0 : a.total_price_pence), 0) }
}

export function timeOffOn(date: string, timeOff: TimeOff[]): TimeOff | null {
  return timeOff.find((o) => o.starts_on <= date && date <= o.ends_on) ?? null
}

// A stretch of the day that is taken, by a booking (with the blocked time
// after it) or by a time block.
interface Span {
  start: number
  end: number
}

// uncovered returns the parts of [start, end) no span covers. spans must be
// sorted by start.
function uncovered(start: number, end: number, spans: Span[]): [number, number][] {
  const parts: [number, number][] = []
  let cursor = start
  for (const s of spans) {
    if (s.end <= cursor || s.start >= end) {
      continue
    }
    if (s.start > cursor) {
      parts.push([cursor, s.start])
    }
    cursor = Math.max(cursor, s.end)
  }
  if (cursor < end) {
    parts.push([cursor, end])
  }
  return parts
}

// dayPlan turns one day's working hours, time off and appointments into the
// agenda: bookings in time order, with free stretches and breaks between them.
// A time block gets its own row and splits the free stretch around it, but is
// not a booking, so it adds nothing to the count.
export function dayPlan(
  date: string,
  windows: OpeningWindow[],
  timeOff: TimeOff[],
  appointments: Appointment[],
  timeBlocks: TimeBlock[] = [],
): DayPlan {
  const bookings = appointments
    .filter((a) => a.date === date && a.status !== 'cancelled')
    .sort((a, b) => a.start_minute - b.start_minute)
  const blocks = timeBlocks.filter((b) => b.date === date).sort((a, b) => a.start_minute - b.start_minute)
  const taken: Span[] = [
    ...bookings.map((b) => ({ start: b.start_minute, end: Math.max(b.end_minute, b.blocked_until_minute) })),
    ...blocks.map((b) => ({ start: b.start_minute, end: b.end_minute })),
  ].sort((a, b) => a.start - b.start)
  const off = timeOffOn(date, timeOff)
  const weekday = weekdayIndex(date)
  const dayWindows = windows
    .filter((w) => w.weekday === weekday)
    .sort((a, b) => a.start_minute - b.start_minute)

  const rows: DayRow[] = bookings.map((appointment) => ({ kind: 'booking', start: appointment.start_minute, appointment }))
  for (const b of bookings) {
    if (b.blocked_until_minute > b.end_minute) {
      rows.push({ kind: 'blocked', start: b.end_minute, end: b.blocked_until_minute })
    }
  }
  for (const block of blocks) {
    rows.push({ kind: 'timeBlock', start: block.start_minute, end: block.end_minute, block })
  }
  if (!off) {
    dayWindows.forEach((w, i) => {
      for (const [start, end] of uncovered(w.start_minute, w.end_minute, taken)) {
        if (end - start >= minFreeMinutes) {
          rows.push({ kind: 'free', start, end })
        }
      }
      const next = dayWindows[i + 1]
      if (next && next.start_minute > w.end_minute) {
        for (const [start, end] of uncovered(w.end_minute, next.start_minute, taken)) {
          rows.push({ kind: 'break', start, end })
        }
      }
    })
  }
  const order = (r: DayRow) => (r.kind === 'booking' ? 0 : r.kind === 'blocked' ? 1 : r.kind === 'timeBlock' ? 2 : 3)
  rows.sort((a, b) => a.start - b.start || order(a) - order(b))

  return {
    closed: !off && dayWindows.length === 0,
    timeOff: off,
    rows,
    ...bookedSummary(bookings),
  }
}

// A block is an hour long unless something is in the way; it never runs past
// the end of the day (a block ends by 24:00 and the last start is 23:55).
const defaultBlockMinutes = 60
const lastBlockEnd = 1435

// blockDefaultEnd is where a new block starting at startMinute ends: an hour
// later, cut short at the next booking or block, the start of a break or
// closing time. Outside her hours (a closed day, or after closing) only the
// bookings, blocks and the end of the day limit it.
export function blockDefaultEnd(
  date: string,
  startMinute: number,
  windows: OpeningWindow[],
  appointments: Appointment[],
  timeBlocks: TimeBlock[],
): number {
  const limits = [startMinute + defaultBlockMinutes, lastBlockEnd]
  for (const a of appointments) {
    if (a.date === date && a.status !== 'cancelled' && a.start_minute > startMinute) {
      limits.push(a.start_minute)
    }
  }
  for (const b of timeBlocks) {
    if (b.date === date && b.start_minute > startMinute) {
      limits.push(b.start_minute)
    }
  }
  const dayWindows = windows.filter((w) => w.weekday === weekdayIndex(date)).sort((a, b) => a.start_minute - b.start_minute)
  const within = dayWindows.find((w) => w.start_minute <= startMinute && startMinute < w.end_minute)
  if (within) {
    limits.push(within.end_minute)
  } else {
    const next = dayWindows.find((w) => w.start_minute > startMinute)
    if (next) {
      limits.push(next.start_minute)
    }
  }
  return Math.min(...limits)
}
